import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

const EMAIL_SUBJECT = 'Action Required: Pending Assignment Corrections';

export default function NotCorrectedAssignmentList() {
  const [staffCards, setStaffCards] = useState([]);
  const [subjectRows, setSubjectRows] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [courseFilter, setCourseFilter] = useState([]);
  const [search, setSearch] = useState('');
  const [activeStaffId, setActiveStaffId] = useState('all');
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [emailModal, setEmailModal] = useState({ open: false, staff: null, subject: EMAIL_SUBJECT, body: '' });
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const copyFeedbackTimeoutRef = useRef(null);

  const [coordinateCourses, setCoordinateCourses] = useState({});
  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [accessLoaded, setAccessLoaded] = useState(false);

  // Load coordinate course access for the logged-in user
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;

        const data = accessRes.data || {};
        const ids = Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : [];
        setCoordinateCourses(data.coordinateCourses || {});
        setAccessibleCourseIds(ids);
        setIsSuperAdmin(!!data.isSuperAdmin);
      } catch (e) {
        console.error('Failed to load coordinate courses for assignment summary', e);
        setAccessibleCourseIds([]);
        setIsSuperAdmin(false);
      } finally {
        if (isMounted) {
          setAccessLoaded(true);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const loadSummary = useCallback(async () => {
    // Wait until coordinate course access info is loaded so we can
    // restrict results for coordinators to their own courses.
    if (!accessLoaded) return;

    // Determine which courses this user can see.
    let accessibleParam = 'all';
    const rawIds = Array.isArray(accessibleCourseIds) ? accessibleCourseIds : [];

    if (!isSuperAdmin) {
      const numericIds = rawIds
        .map((id) => Number(id))
        .filter((num) => Number.isFinite(num) && num > 0);

      if (numericIds.length === 0) {
        // Coordinator with no accessible courses – show empty data set.
        setStaffCards([]);
        setSubjectRows([]);
        setDownloadUrl(null);
        setLoading(false);
        return;
      }

      accessibleParam = numericIds;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/admin/assignment-summary', {
        params: { accessibleCourses: accessibleParam },
      });
      const payload = response?.data || {};
      setStaffCards(payload.staffCards || []);
      setSubjectRows(payload.subjectRows || []);
      setDownloadUrl(payload.downloadUrl || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load pending assignments');
    } finally {
      setLoading(false);
    }
  }, [accessLoaded, accessibleCourseIds, isSuperAdmin]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        window.clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const courses = useMemo(() => {
    // If the user is not a super admin and coordinateCourses are available,
    // show only their coordinated courses in the dropdown, using the
    // accessibleCourseIds list to preserve ordering.
    if (!isSuperAdmin && coordinateCourses && typeof coordinateCourses === 'object') {
      const list = [];
      const seen = new Set();

      const pushCourse = (courseId) => {
        const cid = Number(courseId);
        if (!Number.isFinite(cid) || cid <= 0 || seen.has(cid)) return;
        const perm = coordinateCourses[cid] || coordinateCourses[String(cid)];
        if (!perm) return;
        const name = perm.courseName || perm.fullName || '';
        const short = perm.shortName || perm.courseShortName || '';
        const scheduleType = perm.scheduleType || null;
        if (!name && !short) return;
        seen.add(cid);
        list.push({ id: cid, name: name || short, short: short || name, scheduleType });
      };

      const ids = Array.isArray(accessibleCourseIds) ? accessibleCourseIds : [];
      ids.forEach((id) => pushCourse(id));

      // Fallback: include any remaining coordinate courses not in accessibleCourseIds
      Object.keys(coordinateCourses || {}).forEach((key) => {
        pushCourse(key);
      });

      if (list.length > 0) {
        return list.sort((a, b) => a.name.localeCompare(b.name));
      }
    }

    // Default: derive courses from the subjectRows payload (for super admins
    // or when coordinateCourses are not available).
    const map = new Map();
    subjectRows.forEach((row) => {
      if (!map.has(row.courseId)) {
        map.set(row.courseId, { id: row.courseId, name: row.courseName, short: row.courseShortName });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [subjectRows, coordinateCourses, accessibleCourseIds, isSuperAdmin]);

  useEffect(() => {
    setCourseFilter((prev) => prev.filter((id) => courses.some((course) => course.id === id)));
  }, [courses]);

  const filteredSubjectRows = useMemo(() => {
    return subjectRows.filter((row) => {
      const matchesCourse = courseFilter.length === 0 || courseFilter.includes(row.courseId);
      const matchesStaff = activeStaffId === 'all' || row.staffId === activeStaffId;
      const term = search.trim().toLowerCase();
      const combined = [
        row.courseShortName,
        row.courseName,
        row.batchInternalName,
        row.batchName,
        row.subjectShortName,
        row.subjectName,
        row.staffName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesSearch = term === '' || combined.includes(term);
      return matchesCourse && matchesStaff && matchesSearch;
    });
  }, [subjectRows, courseFilter, activeStaffId, search]);

  const filteredStaffCards = useMemo(() => {
    if (courseFilter.length === 0) {
      return [];
    }

    return staffCards
      .map((staff) => {
        if (activeStaffId !== 'all' && staff.staffId !== activeStaffId) {
          return null;
        }

        const filteredSubjects = (staff.subjects || []).filter((subject) => {
          return filteredSubjectRows.some(
            (row) => row.subjectId === subject.subjectId && row.staffId === staff.staffId
          );
        });

        if (search.trim()) {
          const term = search.trim().toLowerCase();
          const filteredBySearch = filteredSubjects.filter((subject) => {
            const combined = [subject.subjectShortName, subject.subjectName]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return combined.includes(term);
          });

          const newTotal = filteredBySearch.reduce((sum, subject) => sum + (subject.totalPending || 0), 0);
          return { ...staff, subjects: filteredBySearch, totalPending: newTotal };
        }

        const newTotal = filteredSubjects.reduce((sum, subject) => sum + (subject.totalPending || 0), 0);
        return { ...staff, subjects: filteredSubjects, totalPending: newTotal };
      })
      .filter((staff) => staff !== null && staff.totalPending > 0);
  }, [staffCards, filteredSubjectRows, activeStaffId, search, courseFilter]);

  // Debug: log filtered data for console inspection
  useEffect(() => {
    // Log sizes and a preview to avoid noisy dumps
    const previewStaff = filteredStaffCards.slice(0, 5).map((s) => ({
      staffId: s.staffId,
      staffName: s.staffName,
      totalPending: s.totalPending,
      subjects: (s.subjects || []).slice(0, 3).map((subj) => ({
        subjectId: subj.subjectId,
        name: subj.subjectShortName || subj.subjectName,
        totalPending: subj.totalPending,
      })),
    }));

    const previewSubjects = filteredSubjectRows.slice(0, 5).map((r) => ({
      course: r.courseShortName || r.courseName,
      batch: r.batchInternalName || r.batchName,
      subject: r.subjectShortName || r.subjectName,
      staff: r.staffName,
      totalPending: r.totalPending,
    }));

    console.info('[NCA] filters', {
      courseFilter,
      search,
      activeStaffId,
      staffCount: filteredStaffCards.length,
      subjectRowCount: filteredSubjectRows.length,
      staffPreview: previewStaff,
      subjectPreview: previewSubjects,
      filteredStaffCards,
    });
  }, [courseFilter, search, activeStaffId, filteredStaffCards, filteredSubjectRows]);

  const toggleCourse = (courseId) => {
    setCourseFilter((prev) => {
      if (prev.includes(courseId)) {
        return prev.filter((id) => id !== courseId);
      }
      return [...prev, courseId];
    });
  };

  const selectAllCourses = () => {
    setCourseFilter(courses.map((course) => course.id));
  };

  const clearAllCourses = () => {
    setCourseFilter([]);
    setActiveStaffId('all');
  };

  const normalizeDownloadPath = useCallback(() => {
    if (!downloadUrl) return null;
    try {
      const parsed = new URL(downloadUrl, window.location.origin);
      // Strip leading /api because axiosInstance already prefixes baseURL /api
      return parsed.pathname.replace(/^\/api/, '') + parsed.search;
    } catch (e) {
      // Fallback: strip /api if present
      return downloadUrl.replace(/^\/api/, '');
    }
  }, [downloadUrl]);

  const handleDownloadOverall = useCallback(async () => {
    if (!downloadUrl || downloading) return;

    // Mirror the same access control used when loading the summary.
    if (!accessLoaded) return;

    let accessibleParam = 'all';
    const rawIds = Array.isArray(accessibleCourseIds) ? accessibleCourseIds : [];

    if (!isSuperAdmin) {
      const numericIds = rawIds
        .map((id) => Number(id))
        .filter((num) => Number.isFinite(num) && num > 0);

      if (numericIds.length === 0) {
        return;
      }

      accessibleParam = numericIds;
    }

    const path = normalizeDownloadPath();
    if (!path) return;

    const separator = path.includes('?') ? '&' : '?';

    const queryParts = ['format=csv'];
    if (isSuperAdmin) {
      queryParts.push('accessibleCourses=all');
    } else if (Array.isArray(accessibleParam)) {
      accessibleParam.forEach((id) => {
        queryParts.push(`accessibleCourses=${encodeURIComponent(id)}`);
      });
    }

    const apiPath = `${path}${separator}${queryParts.join('&')}`;

    setDownloading(true);
    try {
      const resp = await axiosInstance.get(apiPath, { responseType: 'blob' });
      const contentType = resp.headers['content-type'] || 'text/csv';
      const blob = new Blob([resp.data], { type: contentType });

      let filename = 'pending-assignment-reminder.csv';
      const disposition = resp.headers['content-disposition'];
      if (disposition) {
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
        if (match) {
          filename = decodeURIComponent(match[1] || match[2] || filename);
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download CSV failed', err);
      setError('Failed to download CSV');
    } finally {
      setDownloading(false);
    }
  }, [downloadUrl, downloading, normalizeDownloadPath, accessLoaded, accessibleCourseIds, isSuperAdmin]);

  const openEmailModal = (staff) => {
    const subject = EMAIL_SUBJECT;
    const lines = [];
    lines.push(`Dear ${staff.staffName || 'Lecturer'},`);
    lines.push('');
    lines.push(
      'Our records indicate there are pending assignment corrections in the subjects listed below. To ensure timely release of marks and maintain academic standards, please prioritize these corrections.'
    );
    lines.push('');

    const subjectGroups = new Map();
    (staff.subjects || []).forEach((subject) => {
      const subjectKey = subject.subjectShortName || subject.subjectName || 'Subject';
      const existing = subjectGroups.get(subjectKey) || { pendingHnd: 0, pendingTopup: 0 };
      subjectGroups.set(subjectKey, {
        pendingHnd: existing.pendingHnd + (subject.pendingHnd || 0),
        pendingTopup: existing.pendingTopup + (subject.pendingTopup || 0),
      });
    });

    subjectGroups.forEach((counts, subjectName) => {
      const total = counts.pendingHnd + counts.pendingTopup;
      if (total > 0) {
        lines.push(` • ${subjectName}: ${total} pending (HND: ${counts.pendingHnd}, TOPUP: ${counts.pendingTopup})`);
      }
    });

    lines.push('');
    lines.push('Action requested:');
    lines.push(' • Complete the remaining corrections and update marks in the system.');
    lines.push(' • If you identify any discrepancies, reply to this email with the details.');
    lines.push('');
    lines.push('We appreciate your prompt attention to this matter.');
    lines.push('');
    lines.push('Regards,');
    lines.push('Head of Department');

    const body = lines.join('\n');
    setEmailModal({ open: true, staff, subject, body });
  };

  const copyToClipboard = async (text) => {
    if (!text) return false;
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return true;
      } catch (err) {
        console.error('Clipboard copy failed:', err);
        return false;
      }
    }
  };

  const showCopyFeedback = (label) => {
    if (copyFeedbackTimeoutRef.current) {
      window.clearTimeout(copyFeedbackTimeoutRef.current);
    }
    setCopyFeedback(`${label} copied to clipboard`);
    copyFeedbackTimeoutRef.current = window.setTimeout(() => setCopyFeedback(null), 2000);
  };

  const handleModalCopy = async (value, label) => {
    if (!value) return;
    const success = await copyToClipboard(value);
    if (success && label) {
      showCopyFeedback(label);
    }
  };

  return (
    <DashboardLayout title="Not Corrected Assignment List">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-amber-800">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Pending Assignment Corrections
            </h2>
            <p className="mt-1 text-sm text-amber-900">
              Select one or more courses to view pending assignments. Sorted by highest total pending (HND + TOPUP).
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <button
              onClick={loadSummary}
              className="rounded-lg border border-amber-600 bg-white px-4 py-2 font-medium text-amber-700 transition hover:bg-amber-100"
            >
              Refresh
            </button>
            <button
              onClick={handleDownloadOverall}
              disabled={!downloadUrl || downloading}
              className="rounded-lg border border-amber-600 bg-amber-600 px-4 py-2 font-medium text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {downloading ? 'Downloading…' : 'Download Reminder (.csv)'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600">Loading assignment summary…</div>
        ) : (
          <>
            <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="flex flex-wrap gap-4">
                <div className="flex flex-col">
                  <label className="mb-1 text-xs font-medium text-gray-600">Course</label>
                  <div className="relative">
                    <button
                      onClick={() => setCourseDropdownOpen(!courseDropdownOpen)}
                      className="flex min-w-64 items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
                    >
                      <span className="truncate">
                        {courseFilter.length === 0
                          ? 'Select courses...'
                          : courseFilter.length === 1
                          ? courses.find((course) => course.id === courseFilter[0])?.short ||
                            courses.find((course) => course.id === courseFilter[0])?.name
                          : `${courseFilter.length} courses selected`}
                      </span>
                      <svg className="ml-2 h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {courseDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setCourseDropdownOpen(false)} />
                        <div className="absolute left-0 right-0 z-20 mt-1 max-h-80 overflow-hidden rounded-lg border border-gray-300 bg-white shadow-lg">
                          <div className="sticky top-0 flex items-center justify-between border-b bg-gray-50 px-3 py-2">
                            <span className="text-xs font-medium text-gray-700">
                              {courseFilter.length === 0 ? 'Select courses' : `${courseFilter.length} selected`}
                            </span>
                            <div className="flex gap-1">
                              <button
                                onClick={selectAllCourses}
                                className="rounded px-2 py-0.5 text-xs text-amber-600 hover:bg-amber-100"
                              >
                                All
                              </button>
                              <button
                                onClick={clearAllCourses}
                                className="rounded px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-200"
                              >
                                Clear
                              </button>
                            </div>
                          </div>
                          <div className="max-h-64 space-y-0.5 overflow-y-auto p-1">
                            {courses.map((course) => (
                              <label
                                key={course.id}
                                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50"
                              >
                                <input
                                  type="checkbox"
                                  checked={courseFilter.includes(course.id)}
                                  onChange={() => toggleCourse(course.id)}
                                  className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                />
                                <span className="flex-1">
                                  {course.short ? `${course.short} – ${course.name}` : course.name}
                                  {course.scheduleType && (
                                    <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                                      {String(course.scheduleType).toLowerCase() === 'weekend' ? 'Weekend' : 'Weekday'}
                                    </span>
                                  )}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-medium text-gray-600">Search</label>
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Course / Batch / Staff"
                    className="w-56 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="text-sm text-gray-600">Staff: {filteredStaffCards.length}</div>
            </div>

            {courseFilter.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No Course Selected</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Please select one or more courses from the dropdown above to view pending assignments.
                </p>
              </div>
            ) : (
              <div className="mb-6">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Staff</h3>
                  <div className="text-sm text-gray-600">{filteredStaffCards.length} staff</div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {filteredStaffCards.map((staff) => {
                    const selected = activeStaffId !== 'all' && staff.staffId === activeStaffId;

                    const groupedSubjects = new Map();
                    (staff.subjects || []).forEach((subject) => {
                      const subjectKey = subject.subjectShortName || subject.subjectName || 'Unknown';
                      const existing = groupedSubjects.get(subjectKey) || 0;
                      groupedSubjects.set(subjectKey, existing + (subject.totalPending || 0));
                    });

                    return (
                      <div
                        key={staff.staffId}
                        onClick={() => setActiveStaffId(selected ? 'all' : staff.staffId)}
                        className={`cursor-pointer overflow-hidden rounded-xl border ${
                          selected ? 'border-amber-500 ring-2 ring-amber-200' : 'border-gray-200'
                        } bg-white shadow-sm`}
                      >
                        <div className="flex items-center gap-3 p-4">
                          <img
                            src={
                              getImageURL(staff.imageUrl)
                            }
                            alt={staff.staffName}
                            className="h-12 w-12 rounded-full object-cover ring-1 ring-gray-200"
                          />
                          <div className="min-w-0">
                            <div className="truncate font-medium text-gray-900">{staff.staffName}</div>
                            <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-600">
                              <span className="truncate">{staff.officeEmail || staff.gmail || '-'}</span>
                              {staff.officeEmail && (
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    copyToClipboard(staff.officeEmail);
                                  }}
                                  className="rounded border border-gray-300 px-1 py-0.5 hover:border-amber-500 hover:text-amber-700"
                                >
                                  Copy
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-gray-100 p-4">
                          <div className="mb-2 text-xs font-semibold text-gray-700">Subject-wise pending</div>
                          <div className="flex flex-wrap gap-2">
                            {Array.from(groupedSubjects.entries())
                              .filter(([, count]) => count > 0)
                              .map(([subjectName, count]) => (
                                <span
                                  key={subjectName}
                                  className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800"
                                >
                                  <span className="font-medium">{subjectName}</span>
                                  <span className="rounded bg-white px-1 text-amber-700">{count}</span>
                                </span>
                              ))}
                            {groupedSubjects.size === 0 && (
                              <span className="text-xs text-gray-500">No pending</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between border-t border-gray-100 p-3">
                          <div className="text-sm text-gray-600">
                            Total:{' '}
                            <span className={staff.totalPending > 0 ? 'font-semibold text-red-600' : ''}>
                              {staff.totalPending}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                openEmailModal(staff);
                              }}
                              className="rounded-md border border-amber-600 bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
                            >
                              Generate Email
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-2 text-xs text-gray-400">
              {/* Placeholder: reintroduce subject-wise table here if needed. */}
            </div>
          </>
        )}

        {emailModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
            <div className="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-xl">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <h4 className="text-base font-semibold text-gray-900">Compose Reminder Email</h4>
                <button
                  onClick={() => setEmailModal({ open: false, staff: null, subject: EMAIL_SUBJECT, body: '' })}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-3 p-4">
                {copyFeedback && (
                  <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{copyFeedback}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-16 shrink-0 text-gray-600">To</span>
                  <input
                    value={emailModal.staff?.officeEmail || emailModal.staff?.gmail || ''}
                    readOnly
                    className="flex-1 rounded border border-gray-300 px-3 py-2"
                  />
                  {(emailModal.staff?.officeEmail || emailModal.staff?.gmail) && (
                    <button
                      onClick={() =>
                        handleModalCopy(emailModal.staff?.officeEmail || emailModal.staff?.gmail, 'Recipient email')
                      }
                      className="rounded border border-gray-300 px-2 py-1 text-xs hover:border-amber-500 hover:text-amber-700"
                    >
                      Copy
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-16 shrink-0 text-gray-600">Subject</span>
                  <input
                    value={emailModal.subject}
                    onChange={(event) =>
                      setEmailModal((prev) => ({ ...prev, subject: event.target.value || EMAIL_SUBJECT }))
                    }
                    className="flex-1 rounded border border-gray-300 px-3 py-2"
                  />
                  <button
                    onClick={() => handleModalCopy(emailModal.subject, 'Subject line')}
                    className="rounded border border-gray-300 px-2 py-1 text-xs hover:border-amber-500 hover:text-amber-700"
                  >
                    Copy
                  </button>
                </div>
                <div>
                  <div className="mb-1 text-sm text-gray-600">Body</div>
                  <textarea
                    value={emailModal.body}
                    onChange={(event) => setEmailModal((prev) => ({ ...prev, body: event.target.value }))}
                    rows={12}
                    className="h-64 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  ></textarea>
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => handleModalCopy(emailModal.body, 'Email body')}
                      className="rounded border border-gray-300 px-3 py-1.5 text-xs hover:border-amber-500 hover:text-amber-700"
                    >
                      Copy Body
                    </button>
                    <button
                      onClick={() =>
                        handleModalCopy(
                          [emailModal.subject, emailModal.body].filter(Boolean).join('\n\n'),
                          'Full email'
                        )
                      }
                      className="rounded border border-gray-300 px-3 py-1.5 text-xs hover:border-amber-500 hover:text-amber-700"
                    >
                      Copy All
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
                <button
                  onClick={() => setEmailModal({ open: false, staff: null, subject: EMAIL_SUBJECT, body: '' })}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
